<template>
    <Navbar :show-back-button="true" @back="navigateToDashboard"/>
    <div class="invoices-container">
        <!-- Header -->
        <div class="invoices-header">
            <h2 class="invoices-title">📋 {{ $t('sales.invoiceList.title') }}</h2>
            <div class="header-actions">
                <button class="refresh-btn" @click="fetchInvoices" :disabled="isLoading">
                    <span class="refresh-icon" :class="{ spinning: isLoading }">⟳</span>
                    <span class="refresh-text">{{ isLoading ? $t('sales.invoiceList.loading') : $t('sales.invoiceList.refresh') }}</span>
                </button>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="loading-state">
            <div class="spinner"></div>
            <p>{{ $t('sales.invoiceList.loadingInvoices') }}</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
            <div class="error-icon">⚠️</div>
            <p class="error-message">{{ error }}</p>
            <button class="retry-btn" @click="fetchInvoices">{{ $t('sales.invoiceList.retry') }}</button>
        </div>

        <!-- Empty State -->
        <div v-else-if="invoices.length === 0" class="empty-state">
            <div class="empty-icon">📭</div>
            <h3>{{ $t('sales.invoiceList.emptyTitle') }}</h3>
            <p>{{ $t('sales.invoiceList.emptyMessage') }}</p>
            <router-link to="/sales" class="shop-btn">
                🛒 {{ $t('sales.invoiceList.startShopping') }}
            </router-link>
        </div>

        <!-- Invoices List -->
        <div v-else class="invoices-list">
            <!-- Summary Cards -->
            <div class="summary-cards">
                <div class="summary-card">
                    <div class="summary-icon">📊</div>
                    <div class="summary-info">
                        <span class="summary-label">{{ $t('sales.invoiceList.totalInvoices') }}</span>
                        <span class="summary-value">{{ totalCount }}</span>
                    </div>
                </div>
            </div>

            <!-- Desktop Table View -->
            <div class="table-container desktop-view">
                <table class="invoices-table">
                    <thead>
                        <tr>
                            <th>{{ $t('sales.invoiceList.invoice') }}</th>
                            <th>{{ $t('sales.invoiceList.date') }}</th>
                            <th>{{ $t('sales.invoiceList.comments') }}</th>
                            <th>{{ $t('sales.invoiceList.actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="invoice in paginatedInvoices" :key="invoice.Invoice_Auto">
                            <td class="invoice-number">
                                <span class="number-badge">{{ invoice.NameAct || '-' }}</span>
                            </td>
                            <td class="invoice-date">
                                <span class="date-badge">{{ formatDate(invoice.Dates) }}</span>
                            </td>
                            <td class="invoice-comments">
                                {{ invoice.Comments || $t('sales.invoiceList.noComments') }}
                            </td>
                            <td class="invoice-actions">
                                <button 
                                    class="view-btn" 
                                    @click="viewInvoice(invoice)"
                                    :title="$t('sales.invoiceList.viewDetails')"
                                >
                                    👁️
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Mobile Card View -->
            <div class="mobile-view">
                <div v-for="invoice in paginatedInvoices" :key="invoice.Invoice_Auto" class="invoice-card">
                    <div class="card-header">
                        <div class="card-title">
                            <span class="card-number">{{ invoice.NameAct || '-' }}</span>
                        </div>
                        <div class="card-actions">
                            <button 
                                class="view-btn" 
                                @click="viewInvoice(invoice)"
                                :title="$t('sales.invoiceList.viewDetails')"
                            >
                                👁️
                            </button>
                        </div>
                    </div>
                    
                    <div class="card-body">
                        <div class="card-row">
                            <span class="card-label">{{ $t('sales.invoiceList.date') }}:</span>
                            <span class="card-value">{{ formatDate(invoice.Dates) }}</span>
                        </div>
                        <div class="card-row" v-if="invoice.Comments && invoice.Comments != '-'">
                            <span class="card-label">{{ $t('sales.invoiceList.comments') }}:</span>
                            <span class="card-value">{{ invoice.Comments }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div v-if="totalPages > 1" class="pagination">
                <button 
                    class="page-btn" 
                    @click="currentPage--" 
                    :disabled="currentPage === 1"
                >
                    ‹
                </button>
                <span class="page-info">
                    {{ $t('sales.invoiceList.page') }} {{ currentPage }} {{ $t('sales.invoiceList.of') }} {{ totalPages }}
                </span>
                <button 
                    class="page-btn" 
                    @click="currentPage++" 
                    :disabled="currentPage === totalPages"
                >
                    ›
                </button>
            </div>
        </div>

        <!-- Invoice Detail Modal -->
        <div v-if="showDetailModal" class="modal-overlay" @click="closeDetailModal">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h3>📄 {{ $t('sales.invoiceList.invoiceDetails') }} {{ selectedInvoice?.NameAct || '' }}</h3>
                    <button class="modal-close-btn" @click="closeDetailModal">×</button>
                </div>
                <div class="modal-body" v-if="selectedInvoice">
                    <!-- Loading state for details -->
                    <div v-if="isLoadingDetails" class="loading-details">
                        <div class="spinner-small"></div>
                        <p>{{ $t('sales.invoiceList.loadingDetails') }}</p>
                    </div>
                    
                    <div v-else>
                        <!-- Invoice Header Info -->
                        <div class="invoice-header-info">
                            <div class="info-grid">
                                <div class="info-item">
                                    <label>{{ $t('sales.invoiceList.invoiceNumber') }}:</label>
                                    <span>{{ selectedInvoice.Num || '-' }}</span>
                                </div>
                                <div class="info-item">
                                    <label>{{ $t('sales.invoiceList.date') }}:</label>
                                    <span>{{ formatDate(selectedInvoice.Dates) }}</span>
                                </div>
                                <div class="info-item full-width" v-if="selectedInvoice.Comments">
                                    <label>{{ $t('sales.invoiceList.comments') }}:</label>
                                    <span>{{ selectedInvoice.Comments }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Invoice Items Table -->
                        <div class="invoice-items-section">
                            <h4>📦 {{ $t('sales.invoiceList.invoiceItems') }}</h4>
                            
                            <!-- Desktop Items Table -->
                            <div class="table-container desktop-view">
                                <table class="items-table">
                                    <thead>
                                        <tr>
                                            <th>{{ $t('sales.invoiceList.row') }}</th>
                                            <th>{{ $t('sales.invoiceList.productName') }}</th>
                                            <th>{{ $t('sales.invoiceList.quantity') }}</th>
                                            <th>{{ $t('sales.invoiceList.unit') }}</th>
                                            <th>{{ $t('sales.invoiceList.unitPrice') }}</th>
                                            <th>{{ $t('sales.invoiceList.totalPrice') }}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(item, index) in invoiceDetails" :key="item.idd || index">
                                            <td>{{ index + 1 }}</td>
                                            <td>{{ item.KalaName || '-' }}</td>
                                            <td>{{ item.value1 || 0 }}</td>
                                            <td>{{ item.vahed || '-' }}</td>
                                            <td>{{ formatCurrency(item.Fe || 0) }}</td>
                                            <td>{{ formatCurrency(item.money || 0) }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                                <tfoot>
                                    <tr class="total-row">
                                        <td colspan="4"></td>
                                        <td><strong>{{ $t('sales.invoiceList.grandTotal') }}:</strong></td>
                                        <td><strong>{{ formatCurrency(invoiceTotalAmount) }}</strong></td>
                                    </tr>
                                </tfoot>
                            </div>

                            <!-- Mobile Items Cards -->
                            <div class="mobile-view">
                                <div v-for="(item, index) in invoiceDetails" :key="item.idd || index" class="item-card">
                                    <div class="item-header">
                                        <span class="item-index">#{{ index + 1 }}</span>
                                        <span class="item-name">{{ item.KalaName || '-' }}</span>
                                    </div>
                                    <div class="item-details">
                                        <div class="item-row">
                                            <span class="item-label">{{ $t('sales.invoiceList.quantity') }}:</span>
                                            <span class="item-value">{{ item.value1 || 0 }}</span>
                                        </div>
                                        <div class="item-row">
                                            <span class="item-label">{{ $t('sales.invoiceList.unit') }}:</span>
                                            <span class="item-value">{{ item.vahed || '-' }}</span>
                                        </div>
                                        <div class="item-row">
                                            <span class="item-label">{{ $t('sales.invoiceList.unitPrice') }}:</span>
                                            <span class="item-value">{{ formatCurrency(item.Fe || 0) }}</span>
                                        </div>
                                        <div class="item-row total">
                                            <span class="item-label">{{ $t('sales.invoiceList.totalPrice') }}:</span>
                                            <span class="item-value highlight">{{ formatCurrency(item.money || 0) }}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Mobile Total -->
                                <div class="mobile-total">
                                    <div class="total-row">
                                        <span class="total-label">{{ $t('sales.invoiceList.grandTotal') }}:</span>
                                        <span class="total-value">{{ formatCurrency(invoiceTotalAmount) }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Empty state for items -->
                            <div v-if="invoiceDetails.length === 0" class="empty-items">
                                <p>{{ $t('sales.invoiceList.noItemsFound') }}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <!-- <button class="print-btn" @click="printInvoice(selectedInvoice)">
                        🖨️ {{ $t('sales.invoiceList.printInvoice') }}
                    </button> -->
                    <button class="close-btn" @click="closeDetailModal">{{ $t('sales.invoiceList.close') }}</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { invoiceService } from '@/services/api'
import Navbar from '@/components/Navbar.vue'

export default {
    name: 'InvoicesList',
    components: {
        Navbar
    },
    setup() {
        const router = useRouter()
        const invoices = ref([])
        const isLoading = ref(false)
        const isLoadingDetails = ref(false)
        const error = ref(null)
        const currentPage = ref(1)
        const pageSize = ref(10)
        const showDetailModal = ref(false)
        const selectedInvoice = ref(null)
        const invoiceDetails = ref([])
        const invoiceTotalAmount = ref(0)

        // Computed
        const totalCount = computed(() => invoices.value.length)
        
        const totalAmount = computed(() => {
            return invoices.value.reduce((sum, inv) => sum + (inv.Ac || 0), 0)
        })

        const totalPages = computed(() => {
            return Math.ceil(invoices.value.length / pageSize.value)
        })

        const paginatedInvoices = computed(() => {
            const start = (currentPage.value - 1) * pageSize.value
            const end = start + pageSize.value
            return invoices.value.slice(start, end)
        })

        // Methods
        const navigateToDashboard = () => {
            router.push('/dashboard')
        }

        const fetchInvoices = async () => {
            isLoading.value = true
            error.value = null
            
            try {
                const response = await invoiceService.getInvoices()
                console.log('Invoices response:', response.data)
                
                if (response.data.success) {
                    invoices.value = response.data.invoices || []
                } else {
                    error.value = response.data.message || 'خطا در دریافت فاکتورها'
                }
            } catch (err) {
                console.error('Error fetching invoices:', err)
                error.value = 'خطا در ارتباط با سرور. لطفاً دوباره تلاش کنید.'
            } finally {
                isLoading.value = false
            }
        }

        const viewInvoice = async (invoice) => {
            selectedInvoice.value = invoice
            showDetailModal.value = true
            isLoadingDetails.value = true
            invoiceDetails.value = []
            invoiceTotalAmount.value = 0
            
            try {
                // Pass ac and act as query parameters
                const response = await invoiceService.getInvoiceDetail(
                    invoice.Invoice_Auto,
                    invoice.Ac || 2,
                    invoice.Act || 271
                )
                console.log('Invoice details response:', response.data)
                
                if (response.data.success) {
                    invoiceDetails.value = response.data.details || []
                    invoiceTotalAmount.value = response.data.invoice_info?.total_amount || 0
                } else {
                    console.error('Failed to fetch details:', response.data.message)
                }
            } catch (error) {
                console.error('Error fetching invoice details:', error)
            } finally {
                isLoadingDetails.value = false
            }
        }

        const closeDetailModal = () => {
            showDetailModal.value = false
            selectedInvoice.value = null
            invoiceDetails.value = []
            invoiceTotalAmount.value = 0
            isLoadingDetails.value = false
        }

        const formatCurrency = (amount) => {
            if (!amount && amount !== 0) return '۰ ریال'
            return new Intl.NumberFormat('fa-IR').format(amount) + ' ریال'
        }

        const formatDate = (dateStr) => {
            if (!dateStr) return '-'
            
            if (dateStr.includes('/')) {
                return dateStr
            }
            
            try {
                const date = new Date(dateStr)
                if (isNaN(date.getTime())) return dateStr
                
                return new Intl.DateTimeFormat('fa-IR', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                }).format(date)
            } catch {
                return dateStr
            }
        }

        const getInvoiceTypeText = (act) => {
            const types = {
                279: 'سفارش',
                280: 'فروش',
                281: 'بازگشت',
                282: 'پیش‌فاکتور'
            }
            return types[act] || 'سایر'
        }

        const getInvoiceTypeClass = (act) => {
            const classes = {
                279: 'order',
                280: 'sale',
                281: 'return',
                282: 'proforma'
            }
            return classes[act] || 'other'
        }

        const printInvoice = (invoice) => {
            console.log('Printing invoice:', invoice)
            // Implement print functionality
        }

        // Lifecycle
        onMounted(() => {
            fetchInvoices()
        })

        return {
            invoices,
            isLoading,
            isLoadingDetails,
            error,
            currentPage,
            pageSize,
            totalCount,
            totalAmount,
            totalPages,
            paginatedInvoices,
            showDetailModal,
            selectedInvoice,
            invoiceDetails,
            invoiceTotalAmount,
            navigateToDashboard,
            fetchInvoices,
            viewInvoice,
            closeDetailModal,
            formatCurrency,
            formatDate,
            getInvoiceTypeText,
            getInvoiceTypeClass,
            printInvoice
        }
    }
}
</script>

<style scoped>
.invoices-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    font-family: 'Vazir', 'Tahoma', sans-serif;
    direction: rtl;
    margin-bottom: 50px;
    background: var(--bg);
    min-height: 100vh;
}

/* Header Styles */
.invoices-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding: 16px 24px;
    background: var(--card-bg);
    border-radius: 12px;
    box-shadow: 0 2px 8px var(--shadow-1);
    border: 1px solid var(--card-border);
}

.invoices-title {
    margin: 0;
    font-size: 22px;
    color: var(--heading);
}

.header-actions {
    display: flex;
    gap: 10px;
}

.refresh-btn {
    padding: 8px 16px;
    background: var(--bg);
    border: 1px solid var(--card-border);
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    color: var(--text);
}

.refresh-btn:hover:not(:disabled) {
    background: var(--ring);
    border-color: var(--primary);
}

.refresh-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.refresh-icon {
    display: inline-block;
    font-size: 18px;
}

.spinning {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

/* Loading State */
.loading-state {
    text-align: center;
    padding: 60px 20px;
    color: var(--subtext);
}

.spinner {
    border: 4px solid var(--card-border);
    border-top: 4px solid var(--primary);
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 0 auto 20px;
}

/* Error State */
.error-state {
    text-align: center;
    padding: 40px 20px;
    background: rgba(249, 115, 22, 0.1);
    border-radius: 8px;
    border: 1px solid var(--orange-1);
}

.error-icon {
    font-size: 48px;
    margin-bottom: 16px;
}

.error-message {
    color: var(--orange-1);
    margin-bottom: 16px;
}

.retry-btn {
    padding: 10px 24px;
    background: var(--primary);
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    transition: background 0.2s;
}

.retry-btn:hover {
    background: var(--primary-hover);
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 60px 20px;
    background: var(--card-bg);
    border-radius: 12px;
    box-shadow: 0 2px 8px var(--shadow-1);
    border: 1px solid var(--card-border);
}

.empty-icon {
    font-size: 64px;
    margin-bottom: 16px;
}

.empty-state h3 {
    margin: 0 0 8px 0;
    color: var(--heading);
}

.empty-state p {
    margin: 0 0 20px 0;
    color: var(--subtext);
}

.shop-btn {
    display: inline-block;
    padding: 12px 24px;
    background: var(--primary);
    color: white;
    text-decoration: none;
    border-radius: 8px;
    transition: background 0.2s;
}

.shop-btn:hover {
    background: var(--primary-hover);
}

/* Summary Cards */
.summary-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
}

.summary-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: var(--card-bg);
    border-radius: 12px;
    box-shadow: 0 2px 8px var(--shadow-1);
    border: 1px solid var(--card-border);
}

.summary-icon {
    font-size: 32px;
}

.summary-info {
    display: flex;
    flex-direction: column;
}

.summary-label {
    font-size: 12px;
    color: var(--muted);
}

.summary-value {
    font-size: 18px;
    font-weight: bold;
    color: var(--heading);
}

/* Table Styles */
.table-container {
    background: var(--card-bg);
    border-radius: 12px;
    box-shadow: 0 2px 8px var(--shadow-1);
    overflow-x: auto;
    border: 1px solid var(--card-border);
}

.invoices-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 600px;
}

.invoices-table thead {
    background: var(--bg);
}

.invoices-table th {
    padding: 14px 16px;
    text-align: center;
    font-weight: 600;
    color: var(--heading);
    border-bottom: 2px solid var(--card-border);
    white-space: nowrap;
}

.invoices-table td {
    padding: 14px 16px;
    border-bottom: 1px solid var(--card-border);
    vertical-align: middle;
    color: var(--text);
}

.invoice-row:hover {
    background: var(--ring);
}

/* Mobile Card View */
.mobile-view {
    display: none;
    flex-direction: column;
    gap: 16px;
}

.invoice-card {
    background: var(--card-bg);
    border-radius: 12px;
    box-shadow: 0 2px 8px var(--shadow-1);
    padding: 16px;
    transition: all 0.2s;
    border: 1px solid var(--card-border);
}

.invoice-card:hover {
    box-shadow: 0 4px 12px var(--shadow-2);
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--card-border);
}

.card-title {
    display: flex;
    align-items: center;
    gap: 10px;
}

.card-number {
    font-size: 16px;
    font-weight: 600;
    color: var(--heading);
}

.card-actions {
    display: flex;
    gap: 8px;
}

.card-body {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.card-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.card-label {
    font-size: 13px;
    color: var(--muted);
}

.card-value {
    font-size: 14px;
    color: var(--text);
}

/* Badges */
.number-badge {
    display: inline-block;
    padding: 4px 12px;
    background: var(--ring);
    color: var(--primary);
    border-radius: 4px;
    font-weight: 600;
    font-size: 14px;
}

.date-badge {
    font-size: 14px;
    color: var(--subtext);
}

.type-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
}

.type-badge.order {
    background: rgba(249, 115, 22, 0.2);
    color: var(--orange-1);
}

.type-badge.sale {
    background: rgba(16, 185, 129, 0.2);
    color: var(--green);
}

.type-badge.return {
    background: rgba(249, 115, 22, 0.2);
    color: var(--orange-1);
}

.type-badge.proforma {
    background: var(--ring);
    color: var(--primary);
}

.type-badge.other {
    background: var(--bg);
    color: var(--muted);
}

.amount-badge {
    font-weight: 600;
    color: var(--green);
}

.amount-highlight {
    font-size: 16px;
    font-weight: bold;
    color: var(--green);
}

/* Action Buttons */
.invoice-actions {
    display: flex;
    gap: 6px;
}

.view-btn,
.print-btn {
    padding: 6px 10px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;
    transition: all 0.2s;
}

.view-btn {
    background: var(--ring);
    color: var(--primary);
}

.view-btn:hover {
    background: rgba(59, 130, 246, 0.2);
}

.print-btn {
    background: var(--bg);
    color: var(--subtext);
}

.print-btn:hover {
    background: var(--hover);
}

/* Pagination */
.pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: var(--card-bg);
    border-radius: 12px;
    margin-top: 16px;
    box-shadow: 0 2px 8px var(--shadow-1);
    border: 1px solid var(--card-border);
}

.page-btn {
    padding: 8px 16px;
    background: var(--bg);
    border: 1px solid var(--card-border);
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;
    transition: all 0.2s;
    color: var(--text);
}

.page-btn:hover:not(:disabled) {
    background: var(--ring);
    border-color: var(--primary);
}

.page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.page-info {
    font-size: 14px;
    color: var(--subtext);
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--modal-overlay);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    animation: fadeIn 0.3s ease-out;
}

.modal-content {
    background: var(--card-bg);
    border-radius: 12px;
    max-width: 600px;
    width: 95%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    animation: slideUp 0.3s ease-out;
    border: 1px solid var(--card-border);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid var(--card-border);
}

.modal-header h3 {
    margin: 0;
    color: var(--heading);
}

.modal-close-btn {
    background: none;
    border: none;
    font-size: 28px;
    cursor: pointer;
    color: var(--subtext);
    transition: color 0.2s;
}

.modal-close-btn:hover {
    color: var(--orange-1);
}

.modal-body {
    padding: 24px;
    overflow-y: auto;
}

.detail-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.detail-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.detail-item.full-width {
    grid-column: 1 / -1;
}

.detail-item label {
    font-size: 12px;
    color: var(--muted);
    font-weight: 500;
}

.detail-item span {
    font-size: 16px;
    color: var(--text);
}

.modal-footer {
    padding: 16px 24px;
    border-top: 1px solid var(--card-border);
    display: flex;
    gap: 10px;
    justify-content: flex-end;
}

.close-btn {
    padding: 8px 20px;
    background: var(--bg);
    border: 1px solid var(--card-border);
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.2s;
    color: var(--text);
}

.close-btn:hover {
    background: var(--hover);
}

/* Invoice Header Info */
.invoice-header-info {
    background: var(--bg);
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 20px;
    border: 1px solid var(--card-border);
}

.info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.info-item.full-width {
    grid-column: 1 / -1;
}

.info-item label {
    font-size: 12px;
    color: var(--muted);
    font-weight: 500;
}

.info-item span {
    font-size: 14px;
    color: var(--text);
}

/* Invoice Items Section */
.invoice-items-section {
    margin-top: 16px;
}

.invoice-items-section h4 {
    margin: 0 0 12px 0;
    color: var(--heading);
    font-size: 16px;
}

.items-table {
    width: 100%;
    border-collapse: collapse;
}

.items-table thead {
    background: var(--bg);
}

.items-table th {
    padding: 10px 12px;
    text-align: right;
    font-weight: 600;
    color: var(--heading);
    border-bottom: 2px solid var(--card-border);
    font-size: 13px;
}

.items-table td {
    padding: 10px 12px;
    border-bottom: 1px solid var(--card-border);
    font-size: 13px;
    color: var(--text);
}

.item-row:hover {
    background: var(--ring);
}

.total-row {
    background: var(--bg);
    font-weight: 600;
}

.total-row td {
    padding: 12px;
    border-top: 2px solid var(--card-border);
    color: var(--heading);
}

/* Mobile item cards */
.item-card {
    background: var(--bg);
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 10px;
    border: 1px solid var(--card-border);
}

.item-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 8px;
    border-bottom: 1px solid var(--card-border);
    margin-bottom: 8px;
}

.item-index {
    font-size: 12px;
    color: var(--muted);
}

.item-name {
    font-weight: 600;
    color: var(--heading);
}

.item-details {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.item-row-detail {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.item-label {
    font-size: 12px;
    color: var(--muted);
}

.item-value {
    font-size: 13px;
    color: var(--text);
}

.item-value.highlight {
    color: var(--green);
    font-weight: 600;
}

.item-row-detail.total {
    padding-top: 6px;
    border-top: 1px solid var(--card-border);
    margin-top: 4px;
}

.mobile-total {
    background: var(--bg);
    border-radius: 8px;
    padding: 12px;
    margin-top: 12px;
    border: 1px solid var(--card-border);
}

.total-row-mobile {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.total-label {
    font-weight: 600;
    color: var(--heading);
}

.total-value {
    font-size: 16px;
    font-weight: 700;
    color: var(--green);
}

.empty-items {
    text-align: center;
    padding: 20px;
    color: var(--muted);
}

.loading-details {
    text-align: center;
    padding: 40px 20px;
}

.spinner-small {
    border: 3px solid var(--card-border);
    border-top: 3px solid var(--primary);
    border-radius: 50%;
    width: 30px;
    height: 30px;
    animation: spin 1s linear infinite;
    margin: 0 auto 12px;
}

/* Animations */
@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* ============================================ */
/* RESPONSIVE STYLES */
/* ============================================ */

/* Tablets and smaller screens */
@media (max-width: 992px) {
    .invoices-container {
        padding: 12px;
    }
    
    .invoices-header {
        padding: 12px 16px;
        flex-wrap: wrap;
        gap: 10px;
    }
    
    .invoices-title {
        font-size: 18px;
    }
    
    .summary-cards {
        grid-template-columns: 1fr 1fr;
    }
    
    .summary-card {
        padding: 16px;
    }
    
    .summary-value {
        font-size: 16px;
    }
}

/* Mobile phones */
@media (max-width: 768px) {
    /* Hide table on mobile */
    .desktop-view {
        display: none;
    }
    
    /* Show card view on mobile */
    .mobile-view {
        display: flex;
    }
    
    .invoices-header {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
        padding: 12px;
    }
    
    .invoices-title {
        font-size: 16px;
        text-align: center;
    }
    
    .header-actions {
        justify-content: center;
    }
    
    .refresh-text {
        display: inline;
    }
    
    .summary-cards {
        grid-template-columns: 1fr;
        gap: 10px;
    }
    
    .summary-card {
        padding: 14px 16px;
    }
    
    .summary-icon {
        font-size: 24px;
    }
    
    .summary-value {
        font-size: 14px;
    }
    
    .pagination {
        flex-wrap: wrap;
        gap: 10px;
        padding: 12px;
    }
    
    .page-btn {
        padding: 6px 12px;
        font-size: 14px;
    }
    
    .page-info {
        font-size: 12px;
    }
    
    /* Mobile card styles */
    .invoice-card {
        padding: 12px;
    }
    
    .card-header {
        flex-wrap: wrap;
        gap: 8px;
    }
    
    .card-title {
        flex-wrap: wrap;
        gap: 6px;
    }
    
    .card-number {
        font-size: 14px;
    }
    
    .card-row {
        flex-wrap: wrap;
        gap: 4px;
    }
    
    .card-label {
        font-size: 12px;
    }
    
    .card-value {
        font-size: 13px;
    }
    
    .amount-highlight {
        font-size: 14px;
    }
    
    /* Modal mobile styles */
    .modal-content {
        width: 98%;
        max-height: 95vh;
    }
    
    .detail-grid {
        grid-template-columns: 1fr;
        gap: 12px;
    }
    
    .modal-header h3 {
        font-size: 16px;
    }
    
    .modal-body {
        padding: 16px;
    }
    
    .modal-footer {
        flex-wrap: wrap;
        justify-content: center;
    }
    
    .modal-footer button {
        flex: 1;
        min-width: 100px;
    }
    
    .info-grid {
        grid-template-columns: 1fr;
        gap: 8px;
    }
    
    .info-item.full-width {
        grid-column: 1;
    }
    
    .items-table {
        font-size: 12px;
    }
    
    .items-table th,
    .items-table td {
        padding: 6px 8px;
    }
}

/* Very small screens */
@media (max-width: 480px) {
    .invoices-container {
        padding: 8px;
    }
    
    .invoices-header {
        padding: 10px;
    }
    
    .invoices-title {
        font-size: 14px;
    }
    
    .refresh-btn {
        padding: 6px 12px;
        font-size: 12px;
    }
    
    .refresh-text {
        font-size: 12px;
    }
    
    .summary-card {
        padding: 10px 12px;
    }
    
    .summary-icon {
        font-size: 20px;
    }
    
    .summary-label {
        font-size: 10px;
    }
    
    .summary-value {
        font-size: 12px;
    }
    
    .invoice-card {
        padding: 10px;
    }
    
    .card-number {
        font-size: 12px;
    }
    
    .type-badge {
        font-size: 10px;
        padding: 2px 8px;
    }
    
    .view-btn,
    .print-btn {
        padding: 4px 8px;
        font-size: 14px;
    }
    
    .info-item label {
        font-size: 11px;
    }
    
    .info-item span {
        font-size: 13px;
    }
    
    .item-name {
        font-size: 13px;
    }
}
</style>